import { connect } from 'react-redux';
import { flatten, negate } from 'lodash';

import {
  FilterCounter,
  FilterCounterProps,
} from '@/filter/components/filter-counter';
import { RootState } from '@/app/app-reducer';
import { RentalscapePropertySummaryMode } from '../../api/ariadne/__generated_types__/graphql';
import { isPropertyIdentified } from '../../utils/data-utils';
import { filteredPropertyList } from '../../filter/filter-selectors';

// Rough listing to property conversion ratio from https://deckardtech.atlassian.net/browse/DECK-24293.
const ESTIMATED_LISTING_TO_PROPERTY_RATIO = 0.6;

export interface Property {
  apn?: string;
  listingIds?: string[];
  deckardid: string;
  latestOccupancy?: number;
}

// Accesses property listing ID list in a safe manner; returns empty list if listing IDs are not available.
function getListingIds(property: Property) {
  return property.listingIds ?? [];
}

// Counts the number of unique listing IDs in the input property list.
function numAllListings(propertyList: Property[]) {
  const listingIds = new Set(flatten(propertyList.map(getListingIds)));
  return listingIds.size;
}

// Counts the number of unique identified properties in the input property list.
function numIdentifiedProperties(propertyList: Property[]) {
  const identifiedPropertyDeckardIds = new Set(
    propertyList
      .filter(isPropertyIdentified)
      .map((property) => property.deckardid)
  );
  return identifiedPropertyDeckardIds.size;
}

const isPropertyUnidentified = negate(isPropertyIdentified);

// Counts the number of unique listings for all unidentified properties in the list.
function numUnidentifiedListings(propertyList: Property[]) {
  const listingIds = new Set(
    flatten(propertyList.filter(isPropertyUnidentified).map(getListingIds))
  );
  return listingIds.size;
}

// See https://deckardtech.atlassian.net/browse/DECK-24293 for the rationale behind the business logic.
function mapStateToProps(state: RootState): FilterCounterProps {
  const properties = filteredPropertyList(state);
  const allListings = numAllListings(properties);
  const identifiedProperties = numIdentifiedProperties(properties);

  const summaryMode = state.data.rentalscapeConfig?.propertySummaryMode;

  // Default behavior for active clients.
  if (summaryMode === RentalscapePropertySummaryMode.Identified) {
    return {
      count: identifiedProperties,
      secondaryCount: allListings,
    };
  }

  // Demo mode; estimate properties we might identify for this customer based on the number of listing we know.
  const unidentifiedListings = numUnidentifiedListings(properties);
  return {
    count:
      identifiedProperties +
      Math.round(ESTIMATED_LISTING_TO_PROPERTY_RATIO * unidentifiedListings),
    countLabel: 'estimated properties',
    secondaryCount: allListings,
  };
}

// SmartFilterCounter switches between different display modes (normal with identified properties vs demo with estimated
// properties) based on the value in the Rentalscape config.
export const SmartFilterCounter = connect(mapStateToProps)(FilterCounter);
